import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { IFile, RestBoolean } from './generic';
import { IMeta } from './Meta';
import { NftMenuActionType } from 'constants/account/nft-menu-action-types';
import { ReviewStatus } from 'constants/nfts/blockchains';

export type NftAction = {
  type: NftMenuActionType;
  condition: boolean;
  label: string;
  icon: ReactNode;
  onClick?:
    | (() => void)
    | boolean
    | ((e: ReactMouseEvent<HTMLElement, MouseEvent>) => void | Promise<void>);
  link?: string;
  disabled?: boolean;
};

export type NftActions = {
  actions: {
    mint?: () => Promise<void>;
    edit?: () => void;
    sell?: () => void;
    revoke?: () => void;
    openOriginal?: boolean;
    withdraw?: () => void;
    share?: boolean;
    transfer?: () => void;
    print?: () => void;
    del?: () => void;
  };
  nft: INft;
  asyncActionLoading?: boolean;
  setAsyncActionLoading?: (loading: boolean) => void;
  setShareNftModalOpen?: (open: boolean) => void;
};

export enum ENftVisibility {
  WEBSITE = 0,
  STORE = 1,
  BOTH = 2,
  HIDDEN = 3
}

export interface IProperties {
  name: string;
  value: string;
}

interface ICustomer {
  id: number;
  first_name: string;
  last_name: string;
  email?: string;
  gender: string;
  name: string;
  date_of_birth: string;
  phone: string;
  status: string | number;
  created_at: string;
  updated_at: string;
}

interface ICreatedBy {
  id: number;
  account_type: string;
  address: string;
  title: string;
  user_name: string;
  logo_url: string;
  banner_url: string;
  bio: string;
  twitter: string;
  facebook: string;
  pinterest: string;
  instagram: string;
  linkedin: string;
  website: string;
  verified_request: number;
  verified: number;
  customer: ICustomer;
  created_at: string;
  updated_at: string;
}

export interface INft {
  id: number;
  media: {
    small: string;
    medium: string;
    large: string;
    original: string;
  };
  preview: null | {
    small: string;
    medium: string;
    large: string;
    original: string;
  };
  sku: string;
  name: string;
  url_key: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: null;
  short_description: string;
  description: string;
  base_price: string | number;
  price: string | number;
  tax_inclusive_price: number;
  formatted_tax_inclusive_price: string;
  visibility: ENftVisibility;
  currency: string;
  formatted_price: string;
  blockchain: string;
  contract_address: string;
  token_standard: string;
  external_url: string;
  token_id?: string;
  token_metadata: string;
  lazy_mint: number;
  status:
    | 'mint_on_sale'
    | 'mint_lazy'
    | 'mint_pending'
    | 'mint_init'
    | 'mint_confirmed'
    | 'mint_error'
    | 'mint_on_withdraw'
    | 'withdraw_pending'
    | 'withdraw_init'
    | 'withdraw_confirmed';
  review_status: ReviewStatus;
  is_gift_included: boolean;
  is_operator_approved: RestBoolean;
  in_house: RestBoolean;
  is_saleable: number;
  is_recently_sold: RestBoolean;
  properties?: IProperties[];
  created_by: ICreatedBy;
  creation_type: number | undefined | null;
  owned_by: ICreatedBy;
  utility_product?: {
    name: string;
    sku: string;
    description: string;
    material: string;
    images: UtilityImage[];
    weight: string;
    width: string;
    height: string;
    length: string;
    pieces: number;
    size: string;
    region: string;
  };
  collection: {
    id: number;
    name: string;
    description: string;
    logo_image: null | string;
    banner_url: string;
    featured_image: string | null;
    website: string | null;
    discord: string | null;
    medium: string | null;
    pinterest: string | null;
    instagram: string | null;
    telegram: string | null;
    slug: string;
    meta_title: string;
    meta_description: string;
    meta_keywords: string | null;
    blockchain: string | null;
    contract_address: string | null;
    total_supply: number;
    category: {
      id: number;
      name: string;
      description: string;
      banner_url: string | null;
      logo_image: string | null;
      featured_image: string | null;
      slug: string;
      meta_title: string;
      meta_description: string;
      meta_keywords: string;
      created_at: string;
      updated_at: string;
    };
    creator: ICreatedBy;
    created_at: string;
    updated_at: string;
    chain_address: string | null;
  };
  created_at: string;
  updated_at: string;
  viewType: string;
  sold_at: string;
  listed_at: string;
  wallet_address: string;
  deleted_at: string | null;
  deleted_reason: string | null;
}

type UtilityImage = {
  id: string | number;
  small: string;
  medium: string;
  large: string;
  original: string;
};

export type IEditNftValidateImageParams = {
  id?: string;
  images?: Record<string, IFile['file']['originFileObj']>;
};

export interface ICreateNftParams {
  creation_type?: null | 0 | 1;
  id?: number | null;
  media?: {
    small: string;
    medium: string;
    large: string;
    original: string;
  } | null;
  preview?: {
    small: string;
    medium: string;
    large: string;
    original: string;
  } | null;
  sku: string;
  name: string;
  short_description: string;
  description: string;
  price: string;
  token_standard: string;
  url_key: string;
  lazy_mint: number;
  meta_title?: string;
  meta_description?: string;
  meta_keywords?: string;
  collection_id: number;
  properties?: IProperties[] | string;
}

export interface ICreatorNftListParams {
  id?: number;
  sku?: string;
  name?: string;
  price?: string;
  blockchain?: string;
  token_id?: string;
  created_by_creator_id?: string;
  owned_by_creator_id?: string;
  collector_creator_id?: string;
  url_key?: string;
  category_id?: number;
  collection_id?: number;
  sort?: string;
  order?: string;
  page?: number;
  limit?: number;
  pagination?: boolean;
}

export interface INftsListParams {
  id: number;
  sku: string;
  name: string;
  price: string;
  creation_type: number | null;
  blockchain: string;
  token_id: string;
  created_by_creator_id: string;
  owned_by_creator_id: string;
  url_key: string;
  category_id: string;
  collection_id: string;
  sort: string;
  order: string;
  page: number;
  limit: number;
  pagination: boolean;
  is_saleable: RestBoolean;
  is_recently_sold: RestBoolean;
  visibility: ENftVisibility;
  have_utility_product: RestBoolean;
  currency: string;
}

export interface INFTListResponse {
  data: INft[];
  meta: IMeta;
}

export enum DisplayMediaTypes {
  noPreview = 0,
  previewWithButton = 1,
  previewWithoutButton = 2
}

export interface INftsFilterParams {
  name: string;
  sort: string;
  order: string;
  page: number;
  pagination: boolean;
  limit: number;
}

export interface INftMintRedirectUrlResponse {
  redirect_url: string;
}

export type TNFTActions = {
  edit?: () => void;
  del?: () => void;
  sell?: () => void;
  revoke?: () => void;
  openOriginal?: boolean;
  withdraw?: () => void;
  share?: boolean;
  transfer?: () => void;
  mint?: () => Promise<void>;
  print?: () => void;
};
